import { Plus, Tray } from '@phosphor-icons/react';
import { Tip } from '~/components/ui/tip';
import { cn } from '~/lib/utils';
import { IS_APPLE } from './is-apple';
// This component's ENTIRE appearance — fill, border, ink and all four hover
// brackets — lives in that stylesheet, and the classic header renders this
// button without ever mounting `ChatTopBar`, which is the app's only other
// importer of it. Today it arrives anyway because `app-header.tsx` imports
// that module statically; make `ChatTopBar` a dynamic import for weight (as
// the graph canvas already is) and this would render as a bare unstyled
// button with four invisible spans — and nothing would fail, because the
// component test reads markup, the CSS test reads the file off disk, and a
// browser check for an unstyled button still finds it visible.
import './top-bar.css';

/**
 * Single source for the combo shown on the badge — the window keydown
 * handler that actually fires it lives in `chat-top-bar.tsx`, alongside the
 * New chat one. Keep the two in sync if the combo ever changes.
 *
 * ⌥B rather than the handoff's ⌘B: Firefox reserves Cmd+B for its bookmarks
 * sidebar, so the page would never be delivered the event — and this surface
 * has a standing rule that a badge must never show a combo that does nothing
 * (design doc decision #6, the same reason `NEW_CHAT_SHORTCUT_LABEL` shows
 * ⌥N rather than the design's ⌘N). Option also keeps the two badges
 * consistent with each other, which matters more here than matching the
 * handoff's literal glyph — they sit ten pixels apart.
 */
export const BUILD_SHORTCUT_LABEL = IS_APPLE ? '⌥B' : 'Alt+B';

/** Class suffix per corner, in the order the handoff lists them. */
const CORNERS = ['tl', 'tr', 'bl', 'br'] as const;

interface BuildButtonProps {
  onClick: () => void;
  /** `sm` is the legacy classic header's compact variant — shorter, and with
   *  no shortcut badge, because ⌥B is registered by `ChatTopBar` and that
   *  surface is not mounted in the classic design. A badge there would
   *  advertise a combo that genuinely does nothing, which is the rule this
   *  button's ⌥B-over-⌘B choice already exists to honour. */
  size?: 'default' | 'sm';
  className?: string;
  /** A new build waits for the "which window?" pick: this click starts it in the background (eddy, 2026-10-26). */
  background?: boolean;
}

/**
 * The secondary sibling of `NewChatButton` (2026-09-01 build-button design
 * handoff): a soft brand-blue pill that opens the building window. Where New
 * chat is a filled brand button, this is the outline one — the pair reads as
 * primary/secondary rather than as two equal actions.
 *
 * Everything visual lives in `top-bar.css` under `.eddy-build-btn`, including
 * the four cyan hover brackets, because the effect is four per-corner
 * transforms plus a reduced-motion rule that keeps the fade and drops the
 * travel. The brackets are decorative: `aria-hidden`, no pointer events, and
 * the button's accessible name is the label plus the shortcut badge — the
 * same shape the New chat button has, so `getByRole('button', { name: … })`
 * behaves identically for both.
 */
export function BuildButton({ onClick, size = 'default', className, background = false }: BuildButtonProps) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      data-background={background ? 'true' : undefined}
      aria-label={background ? 'Start the new build in the background' : undefined}
      className={cn('eddy-build-btn', size === 'sm' && 'eddy-build-btn--sm', className)}
    >
      {CORNERS.map((corner) => (
        <span
          key={corner}
          aria-hidden="true"
          className={`eddy-build-corner eddy-build-corner--${corner}`}
        />
      ))}
      <span className="eddy-build-face">
        {background ? <Tray size={15} /> : <Plus size={15} />}
        Build
      </span>
      {size === 'default' && (
        <span className="eddy-build-kbd font-mono">{BUILD_SHORTCUT_LABEL}</span>
      )}
    </button>
  );
  return background ? <Tip label="Start the new build in the background — it waits in the top bar">{button}</Tip> : button;
}
